<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="row vertical-align mt-3 ms-1 me-1">
        <div class="col-sm-6">
          <h3>Regenerere oppgjørsrapport</h3>
          <p>1. BACKUP hele databasen.</p>
          <p>2. Z-report nr inkrementeres av antall. Mulig du må gå inn i databasen for å endre nr etter genereringen slik at nr blir riktig i rekkefølge.</p>
          <p>3. NB! Ved å klikke på "Vis X-rapport" vil eksisterende z-report for den dagen bli slettet først og alle relaterte ordrer blir unsettled.</p>
          <p v-if="accountingSystem">4. NB! Er dagen allerede synket til {{ accountingLabel }}, må bilaget
            <b>krediteres</b> og dagen <b>synkes på nytt</b> etterpå. Regenereringen endrer bare Z-rapporten
            her — regnskapet beholder tallene fra før.</p>

          <div v-if="!loading">
              <div v-if="hasMultiLocations()" class="mt-5">
                <span class="ingredient-title">Velg avdeling</span>
                <select class="form-select" v-model="locationId" @change="showTerminals()">
                  <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
                </select>
              </div>

              <SelectDate ref="selectDate" :label="'Velg dato'" :selectedDate="selectedDateString" />

              <div v-if="terminals.length > 0">
                <SelectTerminal :terminals="terminals" :terminal="selectedTerminal" @setSelectedTerminal="setSelectedTerminal"/>
                <button type="button" class="bo_btn" :class="isObjectEmpty(xReportCounters) ? 'mb-5' : 'mb-0'"
                        @click="showXReport"> Vis X-rapport </button>

                <div v-if="xReportCounters != undefined">
                  <div v-if="isXReportCountersEmpty" class="mt-4 mb-5">
                    <p>
                      Ingen omsetning fra dette kassepunktet siden forrige oppgjør.
                    </p>
                  </div>
                  <div v-else>
                    <ShowXReport :location="getLocation" :terminal="selectedTerminal" :xReportCounters="xReportCounters" :company="company" :vat="vat"/>
                    <button type="button" class="bo_btn" @click="createZReport">Ta dagens oppgjør for {{ getSettleBtnText() }}</button>
                    <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

                    <!-- The success path used to log to the console and show nothing at all, so the
                         operator could not tell a regeneration from a no-op. It is also the moment the
                         accounting reminder is actually needed — a note at the top of the page has been
                         scrolled past by now. -->
                    <div v-if="regenerateResult" class="mt-4 mb-5">
                      <div v-if="!regenerateResult.success" class="message-error">
                        Genereringen av oppgjørsrapport feilet. Ingenting er endret i regnskapet.
                      </div>
                      <div v-else>
                        <div class="message-success">
                          Oppgjørsrapport regenerert for {{ regenerateResult.terminal }},
                          {{ regenerateResult.date }}.
                        </div>

                        <div v-if="accountingSystem" class="alert alert-warning mt-3 mb-0">
                          <b>Husk regnskapet.</b> Er {{ regenerateResult.date }} allerede synket til
                          {{ accountingLabel }}, viser bilaget der tallene <b>fra før</b> regenereringen —
                          og da overdriver regnskapet omsetningen. Gjør begge to, i denne rekkefølgen:
                          <ol class="mb-2 mt-2">
                            <li>
                              <a :href="'/' + accountingPrefix + 'DeleteVouchers'">Krediter bilaget</a>
                              for {{ regenerateResult.date }}
                            </li>
                            <li>
                              <a :href="'/' + accountingPrefix + 'SyncZReport'">Synk Z-rapporten på nytt</a>
                              for {{ regenerateResult.date }}
                            </li>
                          </ol>
                          Synk uten å kreditere først blir avvist — dagen har allerede et bilag.
                          «Valider synkronisering» for datoen viser avviket til det er rettet.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="mt-4 mb-5">
                <p>
                  Ingen kassepunkt er satt opp for denne avdelingen.
                </p>
              </div>
          </div>
        </div>
      </div>
  </div>
</template>
<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import ConfirmDialogue from '@/components/other/ConfirmDialogue.vue'
  import ReportService from "@/services/report/ReportService";
  import LocationUtil from "@/util/LocationUtil";
  import MerchantUtil from "@/util/MerchantUtil";
  import ShowXReport from "@/components/report/xreport/ShowXReport.vue";
  import SelectTerminal from "@/components/report/SelectTerminal.vue";
  import TerminalService from "@/services/terminal/TerminalService";
  import SelectDate from "@/components/other/SelectDate";
  import moment from "moment";

  export default {
    components: {LoadingSpinner, SelectTerminal, ShowXReport, ConfirmDialogue, SelectDate},
    data() {
      return {
        loading: true,

        locationId: '',
        locations: [],
        merchant: '',
        company: {},
        terminals: [],
        selectedTerminal: {},
        vat: {},
        xReportCounters: undefined,
        selectedDate: new Date(),
        selectedDateString: moment().format('YYYY-MM-DD'),
        regenerateResult: null
      };
    },
    computed: {
      getLocation(){
        return LocationUtil.getLocation(this.locationId);
      },
      /**
       * The accounting system for the SELECTED location, or '' when it has none.
       *
       * Per location, not per merchant — a group can run Unimicro at one restaurant and nothing at
       * another, and the reminder must follow the location being regenerated.
       */
      accountingSystem(){
        let location = this.getLocation;
        let system = location ? location.accountingSystem : '';
        return (system && system !== 'NONE') ? system : '';
      },
      accountingLabel(){
        const labels = {UNIMICRO: 'Unimicro', FIKEN: 'Fiken', TRIPLETEX: 'Tripletex'};
        return labels[this.accountingSystem] || this.accountingSystem;
      },
      /** All three vendors' pages are /{vendor}SyncZReport and /{vendor}DeleteVouchers. */
      accountingPrefix(){
        return this.accountingSystem.toLowerCase();
      },
      isXReportCountersEmpty() {
        if(this.xReportCounters.vippsPickupBrutto == 0 &&
            this.xReportCounters.vippsDineInBrutto == 0 &&
            this.xReportCounters.vippsDeliveryBrutto == 0 &&
            this.xReportCounters.vippsDeliveryServiceBrutto == 0 &&

            this.xReportCounters.cardPickupBrutto == 0 &&
            this.xReportCounters.cardDineInBrutto == 0 &&
            this.xReportCounters.cardDeliveryBrutto == 0 &&
            this.xReportCounters.cardDeliveryServiceBrutto == 0 &&

            this.xReportCounters.bankAxeptPickupBrutto == 0 &&
            this.xReportCounters.bankAxeptDineInBrutto == 0 &&
            this.xReportCounters.bankAxeptDeliveryBrutto == 0 &&

            this.xReportCounters.cashPickupBrutto == 0 &&
            this.xReportCounters.cashDineInBrutto == 0 &&
            this.xReportCounters.cashDeliveryBrutto == 0 &&

            this.xReportCounters.refundReceiptsCount == 0 &&
            this.xReportCounters.refundReceiptsTotalAmount == 0){
          return true;
        } else {
          return false;
        }
      }
    },
    mounted() {
      ReportService.getDataForReportGui().then(
          (response) => {
            let dataForGui = response.data;
            //console.log("dataForGui: " +  JSON.stringify(dataForGui));
            this.locations = dataForGui.locations;
            this.company = dataForGui.company;
            this.vat = dataForGui.vat;
            this.merchant = dataForGui.merchant;
            this.locationId = this.locations.length ? this.locations[0].id : '';

            this.loading = false;
            this.showTerminals();
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      showTerminals() {
        this.loading = true;
        this.xReportCounters = undefined; //clear the previous location's report when switching
        this.regenerateResult = null; //a reminder naming the wrong date is worse than none

        TerminalService.getTerminalsForLocation(this.locationId).then(
            (response) => {
              let filteredTerminals = [];

              let availableTerminals = response.data;
              for(let i = 0; i < availableTerminals.length; i++) {
                let terminal = availableTerminals[i];
                if(terminal.terminalType != 'KDS'){
                  filteredTerminals.push(terminal);
                }
              }

              if(this.useFunctionality() === 'TAKEAWAY' || this.useFunctionality() === 'KIOSK_TAKEAWAY' || this.useFunctionality() === 'ALL'){
                let webshop = {
                  terminalId: 'WEBSHOP',
                  locationId: this.locationId
                };
                filteredTerminals.push(webshop);
              }

              this.terminals = filteredTerminals;
              this.selectedTerminal = this.terminals.length > 0 ? this.terminals[0] : {};

              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false; //never leave the spinner hanging on error
            }
        );
      },
      setSelectedTerminal(terminal){
        this.selectedTerminal = terminal;
        this.regenerateResult = null;
      },
      useFunctionality(){
        return MerchantUtil.useFunctionality();
      },
      getSettleBtnText(){
        if(this.selectedTerminal.terminalId == 'WEBSHOP'){
          return 'nettbutikken';
        } else {
          return this.selectedTerminal.deviceName;
        }
      },
      showXReport() {
        this.loading = true;
        this.regenerateResult = null;

        this.selectedDateString = this.$refs.selectDate.getSelectedDate();
        this.selectedDate = moment(this.selectedDateString, 'YYYY-MM-DD').toDate();
        console.log("selectedDate: " + this.selectedDateString);

        ReportService.getXReportForDate(this.selectedTerminal.locationId, this.selectedTerminal.terminalId, this.selectedDateString).then(
            (response) => {
              this.xReportCounters= response.data;
              //console.log('xReportCounters: ' +  JSON.stringify(this.xReportCounters));
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false; //never leave the spinner hanging on error
            }
        );
      },
      isObjectEmpty(jsonObject){
        return JSON.stringify(jsonObject) === '{}';
      },
      async createZReport(){
        let title = 'Ta dagens oppgjør for ' + this.getSettleBtnText();
        const ok = await this.$refs.confirmDialogue.show({
          title: title,
          message: 'Vil du generere oppgjørsrapport for dette kassepunktet ' + this.getSettleBtnText() + '?',
          okButton: 'Ja',
          cancelButton: 'Avbryt'
        })
        if(ok) {
          let terminal = this.getSettleBtnText();
          let date = moment(this.selectedDateString, 'YYYY-MM-DD').format('DD.MM.YYYY');
          ReportService.createZReportForDate(this.selectedTerminal.locationId, this.selectedTerminal.terminalId, this.selectedDateString).then(
              (response) => {
                if(response.data != null){ //Z-report is created, response.data :== documentId
                  console.log("DONE Generering av oppgjørsrapport for kassepunktet.");
                  this.regenerateResult = {success: true, terminal: terminal, date: date};
                }else {
                  console.log("Generering av oppgjørsrapport for kassepunktet feiler.");
                  this.regenerateResult = {success: false, terminal: terminal, date: date};
                }
              },
              (error) => {
                console.log(error);
                this.regenerateResult = {success: false, terminal: terminal, date: date};
              }
          );
        } else {
          //Cancel, do nothing
        }
      }
    }
  };
</script>
